import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Grid,
  Divider,
  IconButton,
  Chip,
  Tab,
  Tabs,
  Paper,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Close as CloseIcon,
  Event as EventIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Group as GroupIcon,
  CalendarToday as CalendarIcon,
  Phone as PhoneIcon,
  Receipt as ReceiptIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Notes as NotesIcon,
  AccessTime as TimeIcon,
  Lock as LockIcon,
  AddCircleOutline as ExtraIcon,
} from "@mui/icons-material";
import api from "../../services/api";
import { formatDateLong, formatDateShort, getCurrentISOString } from "../../utils/dateUtils";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`colab-tabpanel-${index}`} {...other}>
      {value === index && <Box sx={{ py: { xs: 1, sm: 2 } }}>{children}</Box>}
    </div>
  );
}

const ReadOnlyField = ({ label, value, icon }) => (
  <Paper
    variant="outlined"
    sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(255,255,255,0.03)", height: "100%" }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
      {icon && (
        <Box sx={{ color: "text.disabled", mt: 0.3, flexShrink: 0 }}>{icon}</Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontWeight: 700, textTransform: "uppercase", fontSize: "0.65rem", letterSpacing: 0.5 }}
        >
          {label}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500, color: "text.primary", mt: 0.3, wordBreak: "break-word" }}>
          {value || "—"}
        </Typography>
      </Box>
      <LockIcon sx={{ fontSize: 14, color: "text.disabled", opacity: 0.4, flexShrink: 0 }} />
    </Box>
  </Paper>
);

const ESTADO_COLOR = {
  "Por Confirmar": "warning",
  Confirmado: "primary",
  "En curso": "info",
  Completado: "success",
  Cancelado: "error",
};

const ColaboradorEventoModal = ({ open, onClose, evento }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState("");

  // Gastos
  const [gastos, setGastos] = useState([]);
  const [loadingGastos, setLoadingGastos] = useState(false);
  const [nuevoGasto, setNuevoGasto] = useState({ descripcion: "", monto: "" });
  const [addingGasto, setAddingGasto] = useState(false);

  // Hora extra
  const [horaExtra, setHoraExtra] = useState("");
  const [montoExtra, setMontoExtra] = useState("");
  const [savingExtra, setSavingExtra] = useState(false);
  const [extraGuardado, setExtraGuardado] = useState(false);

  useEffect(() => {
    if (!open) {
      setActiveTab(0);
      setError("");
      setHoraExtra("");
      setMontoExtra("");
      setExtraGuardado(false);
    }
  }, [open]);

  // Cargar gastos
  useEffect(() => {
    const loadGastos = async () => {
      if (!evento?.id || !open) return;
      try {
        setLoadingGastos(true);
        const res = await api.get(`/gastos?evento_id=${evento.id}`);
        const lista = res.data?.data || res.data?.gastos || [];
        setGastos(Array.isArray(lista) ? lista : []);
      } catch {
        setGastos([]);
      } finally {
        setLoadingGastos(false);
      }
    };
    loadGastos();
  }, [evento?.id, open]);

  const totalGastos = gastos.reduce((sum, g) => sum + parseFloat(g.monto || 0), 0);

  const handleAddGasto = async () => {
    if (!nuevoGasto.descripcion.trim() || !nuevoGasto.monto) {
      setError("La descripción y monto del gasto son requeridos");
      return;
    }
    try {
      setAddingGasto(true);
      const payload = {
        evento_id: evento.id,
        descripcion: nuevoGasto.descripcion.trim(),
        monto: parseFloat(nuevoGasto.monto),
      };
      const res = await api.post("/gastos", payload);
      const nuevoId = res.data?.data?.gasto_id || res.data?.gasto_id || Date.now();
      setGastos((prev) => [...prev, { ...payload, id: nuevoId, fecha: getCurrentISOString() }]);
      setNuevoGasto({ descripcion: "", monto: "" });
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || "Error al agregar gasto");
    } finally {
      setAddingGasto(false);
    }
  };

  const handleDeleteGasto = async (gastoId) => {
    try {
      await api.delete(`/gastos/${gastoId}`);
      setGastos((prev) => prev.filter((g) => g.id !== gastoId));
    } catch (err) {
      setError(err.response?.data?.message || "Error al eliminar gasto");
    }
  };

  const handleGuardarHoraExtra = async () => {
    if (!horaExtra || !montoExtra) {
      setError("Ingresa las horas extra y el monto correspondiente");
      return;
    }
    try {
      setSavingExtra(true);

      // 1. Guardar horas_extra y monto_extra en la tabla eventos
      await api.put(`/eventos/${evento.id}`, {
        horas_extra: parseFloat(horaExtra),
        monto_extra: parseFloat(montoExtra),
      });

      // 2. Crear gasto para trazabilidad en el historial
      const gastoPayload = {
        evento_id: evento.id,
        descripcion: `Hora extra: ${horaExtra} hr${parseFloat(horaExtra) !== 1 ? "s" : ""}`,
        monto: parseFloat(montoExtra),
      };
      const res = await api.post("/gastos", gastoPayload);
      const nuevoId = res.data?.data?.gasto_id || res.data?.gasto_id || Date.now();
      setGastos((prev) => [...prev, { ...gastoPayload, id: nuevoId, fecha: getCurrentISOString() }]);
      setExtraGuardado(true);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || "Error al registrar hora extra");
    } finally {
      setSavingExtra(false);
    }
  };

  if (!evento) return null;

  const costo = parseFloat(evento.costo_servicio || 0);
  const anticipo = parseFloat(evento.anticipo || 0);
  const pendiente = costo - anticipo;
  const porcentajePago = costo > 0 ? Math.min(100, Math.round((anticipo / costo) * 100)) : 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { borderRadius: { xs: 0, sm: 2 }, maxHeight: { xs: "100vh", sm: "90vh" } },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "linear-gradient(135deg, #0288d1 0%, #0097a7 100%)",
          color: "white",
          py: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", backdropFilter: "blur(4px)" }}>
            <EventIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {evento.titulo || "Evento"}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {evento.fecha_evento ? formatDateLong(evento.fecha_evento) : "Sin fecha"}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip
            label={evento.estado}
            color={ESTADO_COLOR[evento.estado] || "default"}
            size="small"
            sx={{ fontWeight: "bold", bgcolor: "rgba(255,255,255,0.2)", color: "white", border: "1px solid rgba(255,255,255,0.4)" }}
          />
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "grey.50" }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
        >
          <Tab icon={<EventIcon />} label="General" iconPosition="start"
            sx={{ minHeight: { xs: 42, sm: 48 }, fontSize: { xs: "0.72rem", sm: "0.875rem" } }} />
          <Tab icon={<LocationIcon />} label="Ubicación" iconPosition="start"
            sx={{ minHeight: { xs: 42, sm: 48 }, fontSize: { xs: "0.72rem", sm: "0.875rem" } }} />
          <Tab icon={<GroupIcon />} label="Equipo" iconPosition="start"
            sx={{ minHeight: { xs: 42, sm: 48 }, fontSize: { xs: "0.72rem", sm: "0.875rem" } }} />
          <Tab icon={<MoneyIcon />} label="Finanzas" iconPosition="start"
            sx={{ minHeight: { xs: 42, sm: 48 }, fontSize: { xs: "0.72rem", sm: "0.875rem" } }} />
          <Tab icon={<ReceiptIcon />} label="Gastos" iconPosition="start"
            sx={{ minHeight: { xs: 42, sm: 48 }, fontSize: { xs: "0.72rem", sm: "0.875rem" } }} />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: { xs: 1.5, sm: 3 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>
        )}

        {/* ── Tab General (solo lectura) ── */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main", display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <PersonIcon fontSize="small" /> Información del Cliente
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar sx={{ bgcolor: "success.main", width: 48, height: 48 }}>
                    <PersonIcon sx={{ color: "white" }} />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ color: "success.dark", fontWeight: 700, textTransform: "uppercase", fontSize: "0.65rem" }}>Cliente</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{evento.cliente_nombre || "—"}</Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar sx={{ bgcolor: "info.main", width: 48, height: 48 }}>
                    <PhoneIcon sx={{ color: "white" }} />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ color: "info.dark", fontWeight: 700, textTransform: "uppercase", fontSize: "0.65rem" }}>Teléfono</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{evento.cliente_telefono || "—"}</Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main", display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <EventIcon fontSize="small" /> Información del Evento
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ReadOnlyField label="Título" value={evento.titulo} icon={<EventIcon fontSize="small" />} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2, borderRadius: 2, height: "100%",
                  bgcolor: evento.paquete_nombre || evento.tipo_servicio_nombre ? "primary.main" : "rgba(255,255,255,0.03)",
                  display: "flex", flexDirection: "column", justifyContent: "center",
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: "0.65rem", color: evento.paquete_nombre || evento.tipo_servicio_nombre ? "rgba(255,255,255,0.8)" : "text.secondary" }}>
                  📦 Paquete / Servicio
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: "bold", color: evento.paquete_nombre || evento.tipo_servicio_nombre ? "white" : "text.disabled" }}>
                  {evento.paquete_nombre || evento.tipo_servicio_nombre || "Sin asignar"}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <ReadOnlyField label="Fecha" value={evento.fecha_evento ? formatDateShort(evento.fecha_evento) : null} icon={<CalendarIcon fontSize="small" />} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <ReadOnlyField label="Hora inicio" value={evento.hora_inicio || null} icon={<TimeIcon fontSize="small" />} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <ReadOnlyField label="Hora fin" value={evento.hora_fin || null} icon={<TimeIcon fontSize="small" />} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2, borderRadius: 2, textAlign: "center",
                  bgcolor: evento.duracion_horas ? "success.main" : "rgba(255,255,255,0.03)",
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: "0.65rem", color: evento.duracion_horas ? "rgba(255,255,255,0.8)" : "text.secondary" }}>
                  Horas Contratadas
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: "bold", color: evento.duracion_horas ? "white" : "text.disabled" }}>
                  {evento.duracion_horas ? `${evento.duracion_horas} hrs` : "—"}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ReadOnlyField label="Estado" value={evento.estado} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ReadOnlyField label="Canal" value={evento.canal} />
            </Grid>
            {evento.descripcion && (
              <Grid item xs={12}>
                <ReadOnlyField label="Descripción" value={evento.descripcion} icon={<NotesIcon fontSize="small" />} />
              </Grid>
            )}
          </Grid>
        </TabPanel>

        {/* ── Tab Ubicación (solo lectura) ── */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <ReadOnlyField label="Nombre del Lugar" value={evento.ubicacion} icon={<LocationIcon fontSize="small" />} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ReadOnlyField label="Dirección" value={evento.direccion} icon={<LocationIcon fontSize="small" />} />
            </Grid>

            {/* Mapa + Notas */}
            <Grid item xs={12} sm={7}>
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", minHeight: 280 }}>
                {evento.ubicacion || evento.direccion ? (
                  <Box>
                    <iframe
                      title="Ubicación"
                      width="100%"
                      height="280"
                      frameBorder="0"
                      style={{ border: 0 }}
                      src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(evento.direccion || evento.ubicacion)}`}
                      allowFullScreen
                    />
                    <Box sx={{ p: 1.5, display: "flex", gap: 1.5, justifyContent: "center", bgcolor: "grey.50" }}>
                      <Button size="small" variant="contained" color="primary" startIcon={<LocationIcon />}
                        onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(evento.direccion || evento.ubicacion)}`, "_blank")}>
                        Google Maps
                      </Button>
                      <Button size="small" variant="outlined"
                        sx={{ borderColor: "#33ccff", color: "#00c4ff", "&:hover": { borderColor: "#00c4ff", bgcolor: "rgba(0,196,255,0.08)" } }}
                        onClick={() => window.open(`https://waze.com/ul?q=${encodeURIComponent(evento.direccion || evento.ubicacion)}&navigate=yes`, "_blank")}>
                        🚗 Waze
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <LocationIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">Sin dirección registrada</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} sm={5}>
              {evento.notas ? (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%", borderColor: "warning.light", bgcolor: "rgba(237,108,2,0.04)" }}>
                  <Typography variant="caption" sx={{ color: "warning.dark", fontWeight: 700, textTransform: "uppercase", fontSize: "0.65rem", letterSpacing: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <NotesIcon sx={{ fontSize: 14 }} /> Notas Internas
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic", color: "text.primary", lineHeight: 1.7 }}>
                    {evento.notas}
                  </Typography>
                </Paper>
              ) : (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography variant="body2" color="text.disabled">Sin notas</Typography>
                </Paper>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        {/* ── Tab Equipo (solo lectura) ── */}
        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main", display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <GroupIcon fontSize="small" /> Equipo Asignado
              </Typography>
            </Grid>
            {Array.isArray(evento.colaboradores) && evento.colaboradores.length > 0 ? (
              evento.colaboradores.map((colab, i) => (
                <Grid item xs={12} sm={6} key={colab.id || i}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar sx={{ bgcolor: "primary.main" }}>
                        {(colab.nombre_completo || colab.nombre || "?").charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>{colab.nombre_completo || colab.nombre}</Typography>
                        <Typography variant="caption" color="text.secondary">{colab.especialidad || colab.rol || "Staff"}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 3, textAlign: "center", borderRadius: 2 }}>
                  <GroupIcon sx={{ fontSize: 40, color: "grey.400", mb: 1 }} />
                  <Typography color="text.secondary">Sin colaboradores asignados</Typography>
                </Paper>
              </Grid>
            )}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="caption" color="text.secondary">Registrado por:</Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  avatar={<Avatar sx={{ bgcolor: "primary.main" }}>{(evento.colaborador_nombre || evento.cubierto_por_nombre || "?").charAt(0)}</Avatar>}
                  label={evento.colaborador_nombre || evento.cubierto_por_nombre || "No asignado"}
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
              </Box>
            </Grid>
          </Grid>
        </TabPanel>

        {/* ── Tab Finanzas (lectura + Hora extra editable) ── */}
        <TabPanel value={activeTab} index={3}>
          <Grid container spacing={3}>
            {/* Resumen */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3, borderRadius: 2, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>Monto Total</Typography>
                      <Typography variant="h4" sx={{ fontWeight: "bold" }}>${costo.toLocaleString("es-MX")}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>Anticipo Recibido</Typography>
                      <Typography variant="h4" sx={{ fontWeight: "bold" }}>${anticipo.toLocaleString("es-MX")}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>Pendiente</Typography>
                      <Typography variant="h4" sx={{ fontWeight: "bold", color: pendiente > 0 ? "#ffeb3b" : "#4caf50" }}>
                        ${pendiente.toLocaleString("es-MX")}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Campos de solo lectura */}
            <Grid item xs={12} sm={6}>
              <ReadOnlyField label="Costo del Servicio" value={`$${costo.toLocaleString("es-MX")}`} icon={<MoneyIcon fontSize="small" />} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ReadOnlyField label="Anticipo" value={`$${anticipo.toLocaleString("es-MX")}`} icon={<MoneyIcon fontSize="small" />} />
            </Grid>

            {/* Barra de progreso */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Progreso de Pago</Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>{porcentajePago}%</Typography>
                </Box>
                <Box sx={{ width: "100%", height: 8, bgcolor: "grey.200", borderRadius: 4, overflow: "hidden" }}>
                  <Box sx={{ width: `${porcentajePago}%`, height: "100%", bgcolor: anticipo >= costo ? "success.main" : "primary.main", borderRadius: 4, transition: "width 0.3s ease" }} />
                </Box>
              </Paper>
            </Grid>

            {/* ── Sección Hora Extra (editable) ── */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Paper
                variant="outlined"
                sx={{ p: 2.5, borderRadius: 2, borderColor: extraGuardado ? "success.main" : "warning.main", bgcolor: extraGuardado ? "rgba(76,175,80,0.06)" : "rgba(237,108,2,0.06)" }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: extraGuardado ? "success.dark" : "warning.dark", display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <ExtraIcon fontSize="small" />
                  {extraGuardado ? "✓ Hora extra registrada" : "Registrar Hora Extra"}
                </Typography>
                {extraGuardado ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="body1" sx={{ color: "success.main", fontWeight: 600 }}>
                      {horaExtra} hr{parseFloat(horaExtra) !== 1 ? "s" : ""} — ${parseFloat(montoExtra).toLocaleString("es-MX")}
                    </Typography>
                    <Button size="small" variant="outlined" color="warning"
                      onClick={() => { setExtraGuardado(false); }}>
                      Modificar
                    </Button>
                  </Box>
                ) : (
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Hora extra"
                        type="number"
                        value={horaExtra}
                        onChange={(e) => setHoraExtra(e.target.value)}
                        inputProps={{ min: 0.5, step: 0.5 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end"><TimeIcon fontSize="small" /></InputAdornment>,
                        }}
                        placeholder="Ej: 1.5"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Monto extra"
                        type="number"
                        value={montoExtra}
                        onChange={(e) => setMontoExtra(e.target.value)}
                        inputProps={{ min: 0 }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        placeholder="Ej: 500"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="warning"
                        onClick={handleGuardarHoraExtra}
                        disabled={savingExtra || !horaExtra || !montoExtra}
                        startIcon={savingExtra ? <CircularProgress size={16} /> : <ExtraIcon />}
                      >
                        {savingExtra ? "Guardando..." : "Registrar"}
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* ── Tab Gastos (editable) ── */}
        <TabPanel value={activeTab} index={4}>
          <Grid container spacing={3}>
            {/* Resumen */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3, borderRadius: 2, background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)", color: "white" }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>Total de Gastos</Typography>
                      <Typography variant="h4" sx={{ fontWeight: "bold" }}>${totalGastos.toLocaleString("es-MX")}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>Ganancia Neta</Typography>
                      <Typography variant="h4" sx={{ fontWeight: "bold", color: costo - totalGastos >= 0 ? "#4caf50" : "#ffeb3b" }}>
                        ${(costo - totalGastos).toLocaleString("es-MX")}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Agregar gasto */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>Agregar Gasto</Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Descripción"
                      value={nuevoGasto.descripcion}
                      onChange={(e) => setNuevoGasto((p) => ({ ...p, descripcion: e.target.value }))}
                      placeholder="Ej: Transporte, materiales..."
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Monto"
                      type="number"
                      value={nuevoGasto.monto}
                      onChange={(e) => setNuevoGasto((p) => ({ ...p, monto: e.target.value }))}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={handleAddGasto}
                      disabled={addingGasto}
                      startIcon={addingGasto ? <CircularProgress size={16} /> : <AddIcon />}
                    >
                      Agregar
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Lista de gastos */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
                  Gastos Registrados ({gastos.length})
                </Typography>
                {loadingGastos ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress /></Box>
                ) : gastos.length === 0 ? (
                  <Typography color="text.secondary" sx={{ textAlign: "center", py: 3 }}>No hay gastos registrados.</Typography>
                ) : (
                  <List>
                    {gastos.map((gasto) => {
                      const esAutomatico = gasto.descripcion === "Comisión por Evento Agregado" || gasto.descripcion === "Día Laborado";
                      const esHoraExtra = gasto.descripcion?.startsWith("Hora extra:");
                      return (
                        <ListItem
                          key={gasto.id}
                          sx={{
                            bgcolor: esAutomatico ? "rgba(2,136,209,0.08)" : esHoraExtra ? "rgba(237,108,2,0.08)" : "rgba(255,255,255,0.03)",
                            borderRadius: 1, mb: 1, border: "1px solid",
                            borderColor: esAutomatico ? "rgba(2,136,209,0.3)" : esHoraExtra ? "rgba(237,108,2,0.3)" : "rgba(255,255,255,0.08)",
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: esAutomatico ? "info.main" : esHoraExtra ? "warning.main" : "error.main" }}>
                              {esHoraExtra ? <TimeIcon /> : <ReceiptIcon />}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography component="span" sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                                  {gasto.descripcion}
                                </Typography>
                                {esAutomatico && <Chip label="Auto" size="small" color="info" sx={{ fontSize: "0.6rem", height: 18 }} />}
                                {esHoraExtra && <Chip label="Hora extra" size="small" color="warning" sx={{ fontSize: "0.6rem", height: 18 }} />}
                              </Box>
                            }
                            secondary={gasto.fecha ? formatDateShort(gasto.fecha) : "—"}
                          />
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0, pl: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "error.main", whiteSpace: "nowrap" }}>
                              -${parseFloat(gasto.monto || 0).toLocaleString("es-MX")}
                            </Typography>
                            {!esAutomatico && (
                              <IconButton edge="end" color="error" onClick={() => handleDeleteGasto(gasto.id)} size="small">
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Box>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: "divider" }}>
        <Button onClick={onClose} startIcon={<CloseIcon />} variant="outlined">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColaboradorEventoModal;
